import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { customToast } from '../utils/toastUtils';
import { useUser } from '../contexts/UserContext';
import './AIToolsSidebar.css';

const AIToolsSidebar = ({ isVisible = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData, loading } = useUser();

  const userPlan = userData?.planType || 'free';

  // Access control logic - same as navbar
  const hasAIToolkitAccess = ['gold', 'platinum'].includes(userPlan);
  const hasAICareerMapAccess = userPlan === 'platinum';
  const hasMockInterviewAccess = userPlan === 'platinum';

  // Determine active tool based on current route
  const currentPath = location.pathname;
  const isToolkitActive = currentPath.includes('/ai-career-toolkit');
  const isCareerMapActive = currentPath.includes('/ai-career-map');
  const isMockInterviewActive = currentPath.includes('/take-mock-interview');

  // Show loading state while user data is being fetched
  if (loading && !userData) {
    return (
      <div className="ai-tools-sidebar">
        <div className="sidebar-header">
          <h2>AI Career Tools</h2>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const handleToolClick = (path, hasAccess, toolName) => {
    if (hasAccess) {
      navigate(path);
    } else {
      const planRequired = toolName === 'AI Career Toolkit' ? 'Gold or Platinum' : 'Platinum';
      customToast.warning(`Upgrade to ${planRequired} plan to use ${toolName}`);
    }
  };

  const tools = [
    {
      id: 'toolkit',
      name: 'AI Career Toolkit',
      description: 'Optimize your resume with AI-powered tools',
      path: '/ai-improvement/ai-career-toolkit',
      hasAccess: hasAIToolkitAccess,
      isActive: isToolkitActive,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="3" width="18" height="18" rx="2" fill="url(#toolkitGradient)"/>
          <path d="M8 12h8M12 8v8" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          <circle cx="8" cy="8" r="1.5" fill="white" opacity="0.8"/>
          <circle cx="16" cy="8" r="1.5" fill="white" opacity="0.8"/>
          <circle cx="8" cy="16" r="1.5" fill="white" opacity="0.8"/>
          <circle cx="16" cy="16" r="1.5" fill="white" opacity="0.8"/>
          <defs>
            <linearGradient id="toolkitGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#3b82f6"/>
              <stop offset="100%" stopColor="#1d4ed8"/>
            </linearGradient>
          </defs>
        </svg>
      ),
      color: '#3b82f6'
    },
    {
      id: 'career-map',
      name: 'AI Career Map',
      description: 'Navigate your career path with AI guidance',
      path: '/ai-improvement/ai-career-map',
      hasAccess: hasAICareerMapAccess,
      isActive: isCareerMapActive,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="9" fill="url(#careerMapGradient)"/>
          <path d="M12 3v18M3 12h18" stroke="white" strokeWidth="1.5" opacity="0.5"/>
          <path d="M8 8l8 8M16 8l-8 8" stroke="white" strokeWidth="1.5" opacity="0.3"/>
          <circle cx="12" cy="12" r="3" fill="white"/>
          <circle cx="12" cy="6" r="1.5" fill="white" opacity="0.8"/>
          <circle cx="18" cy="12" r="1.5" fill="white" opacity="0.8"/>
          <circle cx="12" cy="18" r="1.5" fill="white" opacity="0.8"/>
          <circle cx="6" cy="12" r="1.5" fill="white" opacity="0.8"/>
          <defs>
            <linearGradient id="careerMapGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#10b981"/>
              <stop offset="100%" stopColor="#047857"/>
            </linearGradient>
          </defs>
        </svg>
      ),
      color: '#10b981'
    },
    {
      id: 'mock-interview',
      name: 'Take MCQ Test',
      description: 'Role-specific assessments to test your knowledge',
      path: '/ai-improvement/take-mock-interview',
      hasAccess: hasMockInterviewAccess,
      isActive: isMockInterviewActive,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" fill="url(#mockInterviewGradient)"/>
          <path d="M12 7v5.5l3.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="12" cy="12" r="2" fill="white"/>
          <path d="M8 3.5a9 9 0 018 0M8 20.5a9 9 0 008 0" stroke="url(#mockInterviewDarkGradient)" strokeWidth="1.8" strokeLinecap="round"/>
          <defs>
            <linearGradient id="mockInterviewGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ef4444"/>
              <stop offset="100%" stopColor="#dc2626"/>
            </linearGradient>
            <linearGradient id="mockInterviewDarkGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#dc2626"/>
              <stop offset="100%" stopColor="#991b1b"/>
            </linearGradient>
          </defs>
        </svg>
      ),
      color: '#ef4444'
    }
  ];

  return (
    <div className={`ai-tools-sidebar ${isVisible ? 'visible' : 'hidden'}`}>
      <div className="sidebar-header">
        <div className="sidebar-header-content">
          <h2>AI Career Tools</h2>
          <p>Enhance your career with AI-powered features</p>
        </div>

      </div>

      <div className="sidebar-tools">{tools.map((tool) => (
          <div
            key={tool.id}
            className={`tool-card ${tool.isActive ? 'active' : ''} ${!tool.hasAccess ? 'locked' : ''}`}
            onClick={() => handleToolClick(tool.path, tool.hasAccess, tool.name)}
          >
            <div className="tool-icon" style={{ '--tool-color': tool.color }}>
              {tool.icon}
            </div>
            <div className="tool-content">
              <h3>{tool.name}</h3>
              <p>{tool.description}</p>
              {!tool.hasAccess && (
                <span className="lock-badge">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z"/>
                  </svg>
                  Locked
                </span>
              )}
            </div>
            {tool.isActive && <div className="active-indicator"></div>}
          </div>
        ))}
      </div>

      <div className="sidebar-footer">
        {userPlan !== 'platinum' && (
          <button 
            className="upgrade-btn"
            onClick={() => navigate('/pricing')}
          >
            Upgrade to Unlock More
          </button>
        )}
      </div>
    </div>
  );
};

export default AIToolsSidebar;
